import { useState, useRef, useEffect } from "react";
import { svgIcons } from "helpers/images";
import {
  MagnifyingGlassIcon,
  XMarkIcon,
  PlayIcon,
  BuildingOffice2Icon,
  BuildingLibraryIcon,
  Squares2X2Icon,
  ChevronDownIcon,
} from "@heroicons/react/24/outline";
import { useHistory } from "react-router-dom";
import CategoryDropDown from "./CategoryDropDown";
import CapsuleBox from "UI/CapsuleElement";
import { apiMethods, endPoints } from "service/endpoints";
import { request } from "service/request";
import axios from "axios";
import { encodedVideoId } from "helpers";

const GlobalSearch = ({ globalSearchTerm, setGlobalSearchTerm }) => {
  const source = useRef(axios.CancelToken.source());
  const globalSearchRef = useRef();
  const globalSearchInputRef = useRef();
  const globalSearchContainerRef = useRef();
  const closeSearchRef = useRef();
  const categoryRef = useRef();
  const history = useHistory();
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [showCategorySelection, setShowCategorySelection] = useState(false);
  const [showSearchSelection, setShowSearchSelection] = useState(false);
  const [searchList, setSearchList] = useState(null);
  const [isLoadingSearch, setIsLoadingSearch] = useState(false);
  const categoryMap = {
    All: {
      key: "all",
      icon: <Squares2X2Icon className={`h-full w-full aspect-square`} />,
    },
    Video: {
      key: "videos",
      icon: <PlayIcon className={`h-full w-full aspect-square`} />,
    },
    Counselling: {
      key: "counsellings",
      icon: <svgIcons.counselling className={`max-h-full aspect-square`} />,
    },
    Institute: {
      key: "institutes",
      icon: <BuildingOffice2Icon className={`h-full w-full aspect-square`} />,
    },
    University: {
      key: "universities",
      icon: <BuildingLibraryIcon className={`h-full w-full aspect-square`} />,
    },
  };

  useEffect(() => {
    // Set a timeout to update the debounced search term
    const timeoutId = setTimeout(() => {
      setDebouncedSearchTerm(globalSearchTerm);
    }, 500);

    // Cleanup function to clear the timeout if the searchTerm changes before the debounce delay
    return () => {
      clearTimeout(timeoutId);
    };
  }, [globalSearchTerm]);

  // Call the API whenever the debounced search term changes
  useEffect(() => {
    if (debouncedSearchTerm?.length > 2) {
      setIsLoadingSearch(true);
      fetchSearchResults(debouncedSearchTerm);
      setIsLoadingSearch(false);
    }
  }, [debouncedSearchTerm, selectedCategory]);

  useEffect(() => {
    if (globalSearchRef.current && showSearchSelection) {
      document.addEventListener("click", handleClickOutside);
      return () => {
        document.removeEventListener("click", handleClickOutside);
      };
    }
  }, [showSearchSelection]);

  useEffect(() => {
    if (showSearchSelection) {
      const timer = setTimeout(() => {
        if (globalSearchInputRef.current) globalSearchInputRef.current.focus();
      }, 0); // Adjust delay if necessary
      return () => clearTimeout(timer);
    }
  }, [showSearchSelection]);

  const fetchSearchResults = async (searchTerm) => {
    let params = {
      q: searchTerm,
      scope: categoryMap[selectedCategory]?.key,
    };
    let searchResults = await request({
      url: endPoints.dashboard.GET_GLOBAL_SEARCH,
      method: apiMethods.GET,
      showToast: false,
      isLoader: false,
      params: params,
      cancelToken: source.current.token,
    });
    if (searchResults?.data?.results) {
      setSearchList(searchResults?.data?.results);
    }
  };

  const handleClickOutside = (event) => {
    if (
      (globalSearchRef?.current &&
        !globalSearchRef.current?.contains(event.target) &&
        !globalSearchContainerRef.current?.contains(event.target)) ||
      categoryRef.current?.contains(event.target) ||
      closeSearchRef.current?.contains(event.target)
    ) {
      handleCloseSelection();
    }
  };

  const handleSearchClick = () => {
    setShowSearchSelection(true);
  };

  const handleCategoryClick = () => {
    setShowSearchSelection(false);
    setShowCategorySelection(true);
  };

  const handleSearchSelect = (id, type) => {
    let redirectUrl = "";
    switch (type) {
      case "Video":
        redirectUrl = `/portal/videos/${encodedVideoId(`video:${id}`)}`;
        break;
      case "Counselling":
        redirectUrl = `/portal/counsellings/${id}`;
        break;
      case "University":
        redirectUrl = `/portal/universities/${id}`;
        break;
      case "Institute":
        redirectUrl = `/portal/institutes/${id}`;
        break;
      default:
        break;
    }
    window.open(redirectUrl, "_self");
    handleCloseSelection();
  };

  const handleClearSelection = () => {
    setGlobalSearchTerm("");
    setShowSearchSelection(false);
  };

  const handleCloseSelection = () => {
    setShowSearchSelection(false);
  };

  return (
    <div
      className={`relative flex flex-1 min-w-10 max-w-2xl items-center justify-between gap-2 p-2 h-8 bg-white cursor-pointer ${
        showCategorySelection ? "rounded-l-3xl rounded-tr-3xl" : "rounded-3xl"
      }`}
      onClick={handleSearchClick}
    >
      <div className="flex items-center flex-1 min-w-10 text-grey-600 pl-8 pr-2 rounded-3xl">
        <span className="truncate">{globalSearchTerm ? globalSearchTerm : "Search"}</span>
      </div>
      <div className="w-px bg-grey-border h-6"></div>
      <CategoryDropDown
        categoryList={Object.keys(categoryMap)}
        selectedCategory={selectedCategory}
        setSelectedCategory={setSelectedCategory}
        showCategorySelection={showCategorySelection}
        setShowCategorySelection={setShowCategorySelection}
      />
      {showSearchSelection && (
        <div
          className={`absolute top-0 left-0 flex flex-col w-full z-10 bg-white rounded-3xl shadow-search`}
        >
          <div
            ref={globalSearchRef}
            className="relative flex items-center justify-between gap-2 p-2 w-full h-10 "
          >
            <div className="relative flex-1">
              <input
                ref={globalSearchInputRef}
                onChange={(e) => {
                  setGlobalSearchTerm(e.target.value);
                }}
                type="text"
                placeholder={`Search`}
                value={globalSearchTerm}
                className="w-full min-w-20 text-grey-600 pl-8 pr-8 rounded-3xl focus-visible:outline-0 focus:!outline-offset-0"
              />
              <span
                ref={closeSearchRef}
                className={`absolute right-2 top-0.5 w-5 h-5 items-center justify-center rounded-full bg-grey-50 ${
                  showSearchSelection ? "flex" : "hidden"
                } cursor-pointer`}
                onClick={handleClearSelection}
              >
                <XMarkIcon className="w-3 h-3 stroke-medium" />
              </span>
            </div>
            <div className="w-px bg-grey-border h-6"></div>
            <div ref={categoryRef} className="flex h-full items-center">
              <CapsuleBox
                parentClass="p-1 items-center gap-2"
                leadingIconContentClass="items-center justify-center w-full py-1 gap-2 rounded-3xl cursor-pointer"
                trailingIcon={
                  <div className="rounded-full w-6 h-6 bg-white flex items-center justify-center">
                    <ChevronDownIcon className="w-4 mt-[1px]" />
                  </div>
                }
                content={<div className=" text-sm font-bold ">{selectedCategory}</div>}
                onCapsuleClick={handleCategoryClick}
              />
            </div>
            <MagnifyingGlassIcon className="absolute left-4 w-4" />
          </div>
          <div className="h-px bg-grey-border w-[94%] self-center"></div>
          <div
            ref={globalSearchContainerRef}
            className={`flex flex-col w-full h-64 p-2 bg-white rounded-3xl overflow-y-auto scrollbar-hover ${
              globalSearchTerm.length < 2 || !searchList?.length > 0 ? "items-center-center" : ""
            }`}
          >
            {debouncedSearchTerm.length > 0 ? (
              searchList && searchList.length > 0 ? (
                searchList?.map(({ id, name, type }, index) => {
                  return (
                    <CapsuleBox
                      key={index}
                      leadingIconContentClass={`group flex items-center w-full py-[5.6px] pl-2 pr-3 !gap-3 text-grey-700 
                      font-semibold cursor-pointer hover:bg-grey-50 hover:rounded`}
                      leadingIcon={
                        <div
                          className={`flex !w-6 min-w-6 !h-6 rounded-full items-center justify-center bg-grey-50 group-hover:!bg-white`}
                        >
                          <span className="flex items-center justify-center min-w-4 max-w-4 min-h-4 max-h-4 aspect-square">
                            {categoryMap[type]?.icon}
                          </span>
                        </div>
                      }
                      content={
                        <div className="flex flex-col">
                          <p className="text-xs text-grey-600">{type}</p>
                          <p className="text-sm text-grey-700">{name}</p>
                        </div>
                      }
                      onCapsuleClick={() => handleSearchSelect(id, type)}
                    />
                  );
                })
              ) : (
                !isLoadingSearch && <p className="!m-2 text-sm"> No Results Found </p>
              )
            ) : (
              <p className="!m-2 text-sm">
                Start searching for Videos, Institutes, Universities & Counsellings
              </p>
            )}
          </div>
        </div>
      )}
      <MagnifyingGlassIcon className="absolute left-4 m-auto w-4 !text-grey-600" />
    </div>
  );
};
export default GlobalSearch;
